import React, { useCallback, useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import moment from "moment";
import {
  allTagsState,
  appErrorState,
  extraUserInfoByIdState,
  isEditingTagsState,
  newTagsByIdState,
  queryStatsState,
  selectedUserIdsState,
  tagsByIdState,
  tokenState,
  usersState,
} from "state";
import * as api from "api";
import { RequestStatus } from "utils";
import { isEdited } from "utils/isEdited";

import BatchEditTagModal from "components/UserList/BatchEditTagModal";
import NavBar from "./NavBar";
import SearchCriteria from "./SearchCriteria";
import SearchResult from "./SearchResult";
import styles from "./index.module.scss";

const stateKey = "search-user";
const SearchUser = () => {
  const token = useRecoilValue(tokenState);
  const selectedUserIds = useRecoilValue(selectedUserIdsState(stateKey));

  const setAllTags = useSetRecoilState(allTagsState(stateKey));
  const setExtraUserInfoById = useSetRecoilState(
    extraUserInfoByIdState(stateKey)
  );
  const setQueryStats = useSetRecoilState(queryStatsState(stateKey));
  const setAppError = useSetRecoilState(appErrorState(stateKey));

  const [users, setUsers] = useRecoilState(usersState(stateKey));
  const [tagsById, setTagsById] = useRecoilState(tagsByIdState(stateKey));
  const [newTagsById, setNewTagsById] = useRecoilState(
    newTagsByIdState(stateKey)
  );

  const [isEditingTags, setIsEditingTags] = useRecoilState(
    isEditingTagsState(stateKey)
  );

  const [isBatchEditTagModalOpen, setIsBatchEditTagModalOpen] = useState(false);
  const [searchStatus, setSearchStatus] = useState(RequestStatus.Default);

  const startEditingTag = () => {
    let byId: Record<string, Array<string>> = {};
    users.forEach((u) => {
      byId[u._id] = u.tags ? [...u.tags] : [];
    });
    setTagsById({ ...byId });
    setNewTagsById({ ...byId });
    setIsEditingTags(true);
  };

  const saveChangedTags = async () => {
    const { data, error } = await api.updateTags(token)(getEditedUsers());

    if (data) {
      setTagsById({});
      setNewTagsById({});
      setIsEditingTags(false);
      await loadAllTags();
    }

    if (error) {
      setAppError(error);
    }
  };

  const cancelTagsChange = () => {
    setTagsById({});
    setNewTagsById({});
    setIsEditingTags(false);
  };

  const getEditedUsers = () => {
    let edited: Array<{ _id: string; tags: Array<string> }> = [];
    Object.keys(newTagsById).forEach((_id) => {
      if (isEdited({ tags: tagsById[_id], newTags: newTagsById[_id] })) {
        edited = [...edited, { _id, tags: newTagsById[_id] }];
      }
    });

    return edited;
  };

  const search = async (criteria: Criteria) => {
    const startTime = Date.now();
    setSearchStatus(RequestStatus.Pending);
    const { data, error } = await api.searchUser(token)(criteria);

    if (data) {
      const list = data.users;
      setUsers(list);
      let byId: Record<string, ExtraUserInfo> = {};

      list.forEach((user) => {
        let extra: ExtraUserInfo = {};
        if (user.educationExps) {
          extra.degree = Math.max(
            ...user.educationExps.map((exp) => exp.degree)
          );
        }
        if (user.workExps) {
          // lengthOfService
          extra.workStartYear = Math.min(
            ...user.workExps.map((exp) => moment(exp.startTime).valueOf())
          );

          // current position and company
          let currentWorkExp = user.workExps.find(
            (exp) => exp.endTime === null
          );
          if (currentWorkExp) {
            extra.currentPosition = currentWorkExp.position;
            extra.currentCompany = currentWorkExp.company;
          }
        }
        byId[user._id] = extra;
      });

      setExtraUserInfoById(byId);
      setSearchStatus(RequestStatus.Success);
      setQueryStats({
        totalCount: list.length,
        timeElapsed: (Date.now() - startTime) / 1000,
      });
    }

    if (error) {
      setSearchStatus(RequestStatus.Failed);

      console.log(error);
      setAppError(error);

      setQueryStats({ totalCount: 0, timeElapsed: 0 });
    }
  };

  const loadAllTags = useCallback(async () => {
    const { data, error } = await api.getTags(token)();

    if (data) {
      setAllTags(data.tags);
    }
    if (error) {
      console.log(error);
    }
  }, [setAllTags, token]);

  useEffect(() => {
    loadAllTags();
  }, [loadAllTags]);

  return (
    <div className={styles["search-user-page"]}>
      <NavBar
        isEditingTags={isEditingTags}
        editDisabled={!users.length}
        batchEditDisabled={!selectedUserIds.length}
        saveDisabled={!getEditedUsers().length}
        onEdit={startEditingTag}
        onSave={saveChangedTags}
        onCancel={cancelTagsChange}
        onBatchEdit={() => {
          setIsBatchEditTagModalOpen(true);
        }}
      />
      <div className={styles["page-content"]}>
        <SearchCriteria onSearch={search} />
        <SearchResult searchStatus={searchStatus} />
      </div>
      {isBatchEditTagModalOpen && (
        <BatchEditTagModal
          stateKey={stateKey}
          isOpen={isBatchEditTagModalOpen}
          userIds={selectedUserIds}
          onClose={() => setIsBatchEditTagModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SearchUser;
